import React, { useState } from 'react';

const CoursesSection = () => {
  const [activeCategory, setActiveCategory] = useState('advanced');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  const [courseView, setCourseView] = useState('grid'); // 'grid' or 'list'
  
  // Define course categories
  const categories = [
    { id: 'advanced', name: 'Advanced CS' },
    { id: 'foundations', name: 'CS Foundations' },
    { id: 'math', name: 'Mathematics' },
    { id: 'electives', name: 'Electives' }
  ];
  
  // Course data structure
  const coursesData = {
    advanced: [
      {
        id: 'comp3600',
        code: 'COMP 3600',
        title: 'Algorithm Design & Analysis',
        description: 'Advanced study of algorithm design strategies and analysis techniques. Topics include divide-and-conquer, dynamic programming, greedy algorithms, and NP-completeness.',
        semester: 'Winter 2023',
        grade: 'A',
        skills: ['Algorithm Analysis', 'Problem Solving', 'Computational Complexity'],
        projects: ['Algorithmic solution visualizer', 'Performance analysis report'],
        color: '#4C75E0'
      },
      {
        id: 'comp3201',
        code: 'COMP 3201',
        title: 'Nature-Inspired Computing',
        description: 'Exploration of algorithms and computational methods inspired by natural processes and systems, including genetic algorithms, neural networks, and swarm intelligence.',
        semester: 'Fall 2023',
        grade: 'A+',
        skills: ['Genetic Algorithms', 'Neural Networks', 'Swarm Intelligence'],
        projects: ['Evolutionary optimization system', 'Bio-inspired problem solver'],
        color: '#50C878'
      },
      {
        id: 'comp4750',
        code: 'COMP 4750',
        title: 'Intro to Natural Language Processing',
        description: 'Fundamentals of computational linguistics and text processing, including parsing, semantic analysis, and machine learning for language understanding.',
        semester: 'Fall 2023',
        grade: 'A',
        skills: ['Text Processing', 'Language Models', 'Sentiment Analysis'],
        projects: ['Text analyzer application', 'Chatbot implementation'],
        color: '#E07A5F'
      },
      {
        id: 'comp4300',
        code: 'COMP 4300',
        title: 'Intro to Game Programming',
        description: 'Introduction to game architecture, collision detection, physics simulation, and game AI. Development of interactive games using modern engines.',
        semester: 'Fall 2023',
        grade: 'A',
        skills: ['Game Mechanics', 'Physics Simulation', 'Interactive Design'],
        projects: ['2D platformer game', 'Physics-based puzzle game'],
        color: '#FF5252'
      },
      {
        id: 'comp3200',
        code: 'COMP 3200',
        title: 'Algorithmic Techniques for AI',
        description: 'Study of key algorithms and data structures used in artificial intelligence applications, including search, optimization, and learning methods.',
        semester: 'Fall 2024',
        grade: 'A',
        skills: ['AI Algorithms', 'Heuristic Search', 'Optimization'],
        projects: ['Pathfinding visualizer', 'AI decision system'],
        color: '#9C27B0'
      },
      {
        id: 'comp3730',
        code: 'COMP 3730',
        title: 'Intro to Parallel Programming',
        description: 'Introduction to parallel computing architectures, programming models, and performance optimization for multi-core CPUs and GPUs.',
        semester: 'Fall 2024',
        grade: 'A',
        skills: ['Parallel Algorithms', 'Multi-threading', 'Performance Optimization'],
        projects: ['Parallel sorting implementation', 'Multi-threaded application'],
        color: '#009688'
      },
      {
        id: 'comp4759',
        code: 'COMP 4759',
        title: 'Computer Networks',
        description: 'Advanced study of network protocols, routing algorithms, security mechanisms, and distributed systems architecture.',
        semester: 'Fall 2024',
        grade: 'A',
        skills: ['Network Protocols', 'Routing', 'Distributed Systems'],
        projects: ['Protocol analyzer', 'Network simulation'],
        color: '#FF9800'
      },
      {
        id: 'comp4303',
        code: 'COMP 4303',
        title: 'AI in Computer Games',
        description: 'Study of artificial intelligence techniques for game agents, including decision making, planning, learning, and procedural content generation.',
        semester: 'Winter 2025',
        grade: 'In Progress',
        skills: ['Game AI', 'Agent Behavior', 'Procedural Generation'],
        projects: ['AI-driven NPC system', 'Procedural level generator'],
        color: '#673AB7',
        inProgress: true
      },
      {
        id: 'comp4304',
        code: 'COMP 4304',
        title: 'Data Visualization',
        description: 'Techniques and tools for effective visual representation of complex data, including principles of visual perception and interactive visualization.',
        semester: 'Winter 2025',
        grade: 'In Progress',
        skills: ['Visual Design', 'Interactive Graphics', 'Data Interpretation'],
        projects: ['Interactive data dashboard', 'Visual analytics system'],
        color: '#00BCD4',
        inProgress: true
      },
      {
        id: 'comp4768',
        code: 'COMP 4768',
        title: 'Software Dev. for Mobile Devices',
        description: 'Design and implementation of applications for mobile platforms, covering UI/UX principles, data persistence, and device-specific APIs.',
        semester: 'Winter 2025',
        grade: 'In Progress',
        skills: ['Mobile Development', 'UI/UX Design', 'Cross-platform Tools'],
        projects: ['Full-featured mobile app', 'Cross-platform application'],
        color: '#4CAF50',
        inProgress: true
      },
      {
        id: 'comp499a',
        code: 'COMP 499A',
        title: 'Honours Research in Computer Science',
        description: 'Independent research project in computer science under faculty supervision, culminating in a thesis presentation and defense.',
        semester: 'Winter 2025',
        grade: 'In Progress',
        skills: ['Research Methods', 'Technical Writing', 'Advanced Topics'],
        projects: ['Research thesis', 'Conference-style paper'],
        color: '#2196F3',
        inProgress: true
      }
    ],
    foundations: [
      {
        id: 'comp1001',
        code: 'COMP 1001',
        title: 'Intro to Programming',
        color: '#3F51B5'
      },
      {
        id: 'comp1002',
        code: 'COMP 1002',
        title: 'Intro to Logic for Computer Science',
        color: '#00BCD4'
      },
      {
        id: 'comp1003',
        code: 'COMP 1003',
        title: 'Foundations of Computing Systems',
        color: '#8BC34A'
      },
      {
        id: 'comp2001',
        code: 'COMP 2001',
        title: 'Object-Oriented Programming & Human-Computer Interaction',
        color: '#FF5722'
      },
      {
        id: 'comp2002',
        code: 'COMP 2002',
        title: 'Data Structures & Algorithms',
        color: '#9C27B0'
      },
      {
        id: 'comp2003',
        code: 'COMP 2003',
        title: 'Computer Architecture',
        color: '#607D8B'
      },
      {
        id: 'comp2004',
        code: 'COMP 2004',
        title: 'Intro to Operating Systems',
        color: '#E91E63'
      },
      {
        id: 'comp2005',
        code: 'COMP 2005',
        title: 'Software Engineering',
        color: '#FFC107'
      },
      {
        id: 'comp2006',
        code: 'COMP 2006',
        title: 'Computer Networking',
        color: '#795548'
      },
      {
        id: 'comp2007',
        code: 'COMP 2007',
        title: 'Intro to Information Management',
        color: '#009688'
      },
      {
        id: 'comp2008',
        code: 'COMP 2008',
        title: 'Social Issues & Professional Practice',
        color: '#FF9800'
      }
    ],
    math: [
      {
        id: 'math1000',
        code: 'MATH 1000',
        title: 'Calculus I',
        color: '#3F51B5'
      },
      {
        id: 'math1001',
        code: 'MATH 1001',
        title: 'Calculus II',
        color: '#673AB7'
      },
      {
        id: 'math2050',
        code: 'MATH 2050',
        title: 'Linear Algebra I',
        color: '#009688'
      },
      {
        id: 'math2000',
        code: 'MATH 2000',
        title: 'Calculus III',
        color: '#2196F3'
      },
      {
        id: 'stat2500',
        code: 'STAT 2500',
        title: 'Statistics for Business & Arts',
        color: '#4CAF50'
      }
    ],
    electives: [
      {
        id: 'econ1010',
        code: 'ECON 1010',
        title: 'Intro to Microeconomics',
        color: '#FF5722'
      },
      {
        id: 'psyc1000',
        code: 'PSYC 1000',
        title: 'Intro to Psychology',
        color: '#9C27B0'
      },
      {
        id: 'engl1090',
        code: 'ENGL 1090',
        title: 'Critical Reading & Writing: Telling Stories',
        color: '#FFC107'
      },
      {
        id: 'gndr1000',
        code: 'GNDR 1000',
        title: 'Introduction to Gender Studies',
        color: '#E91E63'
      },
      {
        id: 'soci2120',
        code: 'SOCI 2120',
        title: 'Technology & Society',
        color: '#795548'
      }
    ]
  };
  
  // Filter courses based on search query
  const filteredCourses = searchQuery 
    ? Object.values(coursesData).flat().filter(course => 
        course.code.toLowerCase().includes(searchQuery.toLowerCase()) || 
        course.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : coursesData[activeCategory];
  
  // Handle search input
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsFiltering(query.length > 0);
  };
  
  // Handle category selection
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setSelectedCourse(null);
    setSearchQuery('');
    setIsFiltering(false);
  };
  
  // Handle course selection
  const handleCourseClick = (courseId) => {
    setSelectedCourse(selectedCourse === courseId ? null : courseId);
  };
  
  // Get selected course data
  const getSelectedCourseData = () => {
    if (!selectedCourse) return null;
    return Object.values(coursesData).flat().find(course => course.id === selectedCourse);
  };
  
  // Toggle view mode
  const toggleView = () => {
    setCourseView(courseView === 'grid' ? 'list' : 'grid');
  };
  
  // Show course pills (current semester courses)
  const inProgressCourses = Object.values(coursesData.advanced).filter(course => course.inProgress);
  
  return (
    <div className="py-16 px-4 relative">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-purple-500/5 rounded-full filter blur-3xl"></div>
      </div>
      
      {/* Section header */}
      <div className="text-center mb-10 relative">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">Advanced Coursework</h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Specialized academic courses that have shaped my technical knowledge and expertise
        </p>
      </div>
      
      {/* In progress courses pills */}
      {!isFiltering && activeCategory === 'advanced' && (
        <div className="max-w-6xl mx-auto mb-8">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">Currently Taking</h3>
          <div className="flex flex-wrap gap-2">
            {inProgressCourses.map(course => (
              <button
                key={course.id}
                onClick={() => handleCourseClick(course.id)}
                className="flex items-center px-3 py-2 rounded-full text-white text-sm transition-all hover:shadow-lg"
                style={{ 
                  backgroundColor: course.color,
                  boxShadow: selectedCourse === course.id ? `0 0 0 2px white, 0 0 0 4px ${course.color}` : 'none'
                }}
              >
                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                <span className="font-medium">{course.code}</span>
                <span className="mx-2">•</span>
                <span>{course.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Controls: search, category tabs, and view mode */}
      <div className="max-w-6xl mx-auto mb-6 flex flex-col md:flex-row gap-6">
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full px-4 py-3 pl-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        
        {!isFiltering && (
          <div className="flex overflow-x-auto hide-scrollbar">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`px-4 py-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors ${
                  activeCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}
        
        <button
          onClick={toggleView}
          className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          {courseView === 'grid' ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          )}
        </button>
      </div>
      
      {/* Courses display */}
      <div className="max-w-6xl mx-auto">
        {filteredCourses.length > 0 ? (
          <div className={courseView === 'grid' 
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" 
            : "space-y-4"
          }>
            {filteredCourses.map((course) => (
              <div
                key={course.id}
                onClick={() => handleCourseClick(course.id)}
                className={`relative rounded-xl overflow-hidden transition-all cursor-pointer ${
                  courseView === 'grid'
                    ? 'bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:shadow-lg hover:shadow-blue-500/10 hover:border-blue-500/50'
                    : 'bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:border-blue-500/50 p-4 flex items-center'
                } ${selectedCourse === course.id ? 'border-2 border-blue-500 dark:border-blue-500' : ''}`}
              >
                {courseView === 'grid' ? (
                  <>
                    <div 
                      className="h-2 w-full" 
                      style={{ backgroundColor: course.color }}
                    ></div>
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{course.code}</h3>
                          <p className="text-gray-600 dark:text-gray-400">{course.title}</p>
                        </div>
                        {course.grade && (
                          <div className={`px-2 py-1 text-xs rounded-full font-medium ${
                            course.inProgress 
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                              : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                          }`}>
                            {course.grade}
                          </div>
                        )}
                      </div>
                      
                      {course.semester && (
                        <div className="text-sm text-gray-500 dark:text-gray-500 mb-2">
                          {course.semester}
                        </div>
                      )}
                      
                      {course.description && (
                        <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                          {course.description}
                        </p>
                      )}
                      
                      {course.skills && (
                        <div className="flex flex-wrap gap-1 mt-auto">
                          {course.skills.slice(0, 3).map((skill, index) => (
                            <span 
                              key={index} 
                              className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded text-xs"
                            >
                              {skill}
                            </span>
                          ))}
                          {course.skills.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded text-xs">
                              +{course.skills.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div 
                      className="w-4 h-full min-h-[60px] rounded-l mr-4 flex-shrink-0" 
                      style={{ backgroundColor: course.color }}
                    ></div>
                    <div className="flex-grow">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{course.code}</h3>
                          <p className="text-gray-600 dark:text-gray-400">{course.title}</p>
                        </div>
                        {course.grade && (
                          <div className={`px-2 py-1 text-xs rounded-full font-medium ${
                            course.inProgress 
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                              : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                          }`}>
                            {course.grade}
                          </div>
                        )}
                      </div>
                      
                      {course.semester && (
                        <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                          {course.semester}
                        </div>
                      )}
                    </div>
                    <div className="ml-2">
                      <svg 
                        className={`w-5 h-5 text-gray-400 transition-transform ${selectedCourse === course.id ? 'rotate-180' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center p-12 border border-gray-200 dark:border-gray-700 rounded-lg">
            <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl text-gray-600 dark:text-gray-400">No courses match your search</h3>
            <button 
              onClick={() => {setSearchQuery(""); setIsFiltering(false);}}
              className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
            >
              Clear search
            </button>
          </div>
        )}
      </div>
      
      {/* Selected course details panel */}
      {selectedCourse && (
        <div className="max-w-6xl mx-auto mt-8">
          <div 
            className="rounded-xl overflow-hidden bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 transition-all animate-fadeIn"
          >
            {(() => {
              const course = getSelectedCourseData();
              if (!course || !course.description) return null;
              
              return (
                <div className="relative">
                  <div 
                    className="h-3 w-full" 
                    style={{ backgroundColor: course.color }}
                  ></div>
                  <button
                    onClick={() => setSelectedCourse(null)}
                    className="absolute top-4 right-4 p-1 rounded-full bg-gray-100/80 dark:bg-gray-800/80 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row justify-between mb-6">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{course.code}</h3>
                        <p className="text-xl text-gray-600 dark:text-gray-400">{course.title}</p>
                        <div className="flex items-center mt-2">
                          <span className="text-sm text-gray-500 dark:text-gray-500">{course.semester}</span>
                          {course.grade && (
                            <>
                              <span className="mx-2">•</span>
                              <div className={`px-2 py-0.5 text-sm rounded-full font-medium ${
                                course.inProgress 
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                  : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                              }`}>
                                {course.grade}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Course Overview</h4>
                      <p className="text-gray-700 dark:text-gray-300">
                        {course.description}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Skills & Techniques</h4>
                        <div className="flex flex-wrap gap-2">
                          {course.skills.map((skill, index) => (
                            <span 
                              key={index} 
                              className="px-3 py-1 bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Key Projects</h4>
                        <ul className="space-y-2">
                          {course.projects.map((project, index) => (
                            <li key={index} className="flex items-start">
                              <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-gray-700 dark:text-gray-300">{project}</span>
                          </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    {course.inProgress ? (
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 animate-pulse"></div>
                          <span className="text-blue-700 dark:text-blue-300">
                            This course is currently in progress for the {course.semester} semester.
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 flex items-center">
                          <svg className="w-5 h-5 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-green-700 dark:text-green-300">
                            Completed with grade {course.grade} in {course.semester}.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
      
      {/* Course statistics */}
      <div className="max-w-6xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-6 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">11</div>
          <div className="text-gray-600 dark:text-gray-400 text-center">Advanced CS Courses</div>
        </div>
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-6 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">11</div>
          <div className="text-gray-600 dark:text-gray-400 text-center">CS Foundation Courses</div>
        </div>
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-6 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">5</div>
          <div className="text-gray-600 dark:text-gray-400 text-center">Mathematics Courses</div>
        </div>
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-6 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-amber-600 dark:text-amber-400 mb-2">29</div>
          <div className="text-gray-600 dark:text-gray-400 text-center">Total Courses Completed</div>
        </div>
      </div>
      
      {/* Add keyframes for animations */}
      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        `}
      </style>
    </div>
  );
};

export default CoursesSection;